/**
 * NCC: the review dialog (snippets/ncc-review-dialog.liquid) - the one "Leave a review" form for the whole site.
 *
 * Opens on a click of the reviews section's `[data-ncc-write-review]` link, takes a rating, a title, the review, a
 * name and an email on one screen, and posts the review to Judge.me's public reviews endpoint - the same call as Judge.me's public form, with no
 * authentication. With `data-product-id` it is a product review (it lands in that product's pool, the review
 * request emails and the stars on the cards follow from it); without one it is a store review. Judge.me applies
 * the shop's moderation rules, so a review shows on the site once Judge.me publishes it.
 *
 * The dialog is modal (Horizon's scroll lock), the stars are radio buttons (keyboard: arrows), Escape, the
 * backdrop, the close cross and Cancel all close it, and focus returns to the link that opened it.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';

const ENDPOINT = 'https://api.judge.me/api/v1/reviews';

class ReviewDialog extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('dialog'));
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-form]'));
    if (!this.dialog || !this.form) return;

    document.addEventListener('click', this.#handleDocumentClick);
    this.dialog.addEventListener('click', this.#handleBackdropClick);
    this.dialog.addEventListener('cancel', this.#handleCancel);
    this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.form.addEventListener('submit', this.#handleSubmit);
    this.querySelector('[data-stars]')?.addEventListener('change', this.#syncRating);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-ncc-write-review]') : null;
    if (!(link instanceof HTMLElement)) return;
    // one dialog per reviews section: only the link of this dialog's own section opens it
    const section = this.closest('.shopify-section');
    if (section && !section.contains(link)) return;
    event.preventDefault();
    this.open(link);
  };

  /** @param {HTMLElement} [opener] */
  open(opener) {
    const { dialog } = this;
    if (!dialog || dialog.open) return;
    this.#opener = opener || null;
    this.#reset();
    lockScroll(dialog);
    dialog.showModal();
    const first = /** @type {HTMLElement | null} */ (dialog.querySelector('input[type="radio"]'));
    (first || dialog).focus({ preventScroll: true });
  }

  close() {
    const { dialog } = this;
    if (!dialog || !dialog.open) return;
    unlockScroll(dialog);
    dialog.close();
    const opener = this.#opener;
    this.#opener = null;
    if (opener && document.body.contains(opener)) opener.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    // a click on the dialog element itself is the backdrop; the card fills the rest
    if (event.target === this.dialog) this.close();
  };

  /** @param {Event} event */
  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /** the chosen rating -> data-rating on the fieldset */
  #syncRating = () => {
    const stars = this.querySelector('[data-stars]');
    const checked = /** @type {HTMLInputElement | null} */ (stars?.querySelector('input:checked'));
    stars?.closest('[data-rating]')?.setAttribute('data-rating', checked ? checked.value : '0');
  };

  #reset() {
    const { form } = this;
    if (!form) return;
    form.reset();
    form.hidden = false;
    this.#syncRating();
    this.#message('');
    const success = this.querySelector('[data-success]');
    if (success instanceof HTMLElement) success.hidden = true;
    this.#sending(false);
  }

  /** @param {string} text @param {'error' | 'info'} [tone] */
  #message(text, tone = 'error') {
    const el = this.querySelector('[data-message]');
    if (!(el instanceof HTMLElement)) return;
    el.textContent = text;
    el.hidden = !text;
    el.classList.toggle('ncc-review-dialog__message--error', Boolean(text) && tone === 'error');
  }

  /** @param {boolean} on */
  #sending(on) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    const label = this.querySelector('[data-submit-label]');
    if (button) button.disabled = on;
    if (label) {
      if (!label.getAttribute('data-idle')) label.setAttribute('data-idle', label.textContent || '');
      label.textContent = on ? this.dataset.sendingLabel || label.getAttribute('data-idle') : label.getAttribute('data-idle');
    }
  }

  #showSuccess() {
    const { form } = this;
    if (form) form.hidden = true;
    const success = this.querySelector('[data-success]');
    if (success instanceof HTMLElement) {
      success.hidden = false;
      success.focus({ preventScroll: true });
    }
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    const { form } = this;
    if (!form) return;
    const data = new FormData(form);
    const rating = parseInt(String(data.get('rating') || ''), 10);
    const body = String(data.get('body') || '').trim();
    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const title = String(data.get('title') || '').trim();
    const emailField = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="email"]'));
    const ratingOk = rating >= 1 && rating <= 5;
    if (!ratingOk || !body || !name || !email || !(emailField?.checkValidity() ?? true)) {
      this.#message(this.dataset.invalidLabel || '');
      const firstInvalid = /** @type {HTMLElement | null} */ (
        !ratingOk
          ? form.querySelector('input[type="radio"]')
          : !body
            ? form.querySelector('textarea[name="body"]')
            : !name
              ? form.querySelector('input[name="name"]')
              : emailField
      );
      firstInvalid?.focus();
      return;
    }

    // the honeypot: a bot filled the field people never see - pretend it went through and send nothing
    if (String(data.get('website') || '').trim()) {
      this.#showSuccess();
      return;
    }

    this.#message('');
    this.#sending(true);
    const shop = this.dataset.shop || window.location.hostname;
    // form-encoded on purpose: a JSON body needs a CORS preflight and Judge.me answers OPTIONS with a 404, while a
    // simple request goes straight through and the reply carries Access-Control-Allow-Origin: * (measured 2026-09-21).
    // Judge.me validates the email at once (422 "Email must be present") and queues the rest: 201 "Review is being
    // processed in background", so the review shows in the admin a few minutes later.
    const payload = new URLSearchParams({ shop_domain: shop, platform: 'shopify', url: shop, name, email, rating: String(rating), title, body, reviewer_name_format: '' });
    if (this.dataset.productId) payload.set('id', this.dataset.productId);
    try {
      const response = await fetch(ENDPOINT, { method: 'POST', body: payload });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.#showSuccess();
    } catch (error) {
      console.warn('[ncc-review-dialog]', error);
      this.#sending(false);
      this.#message(this.dataset.errorLabel || '');
    }
  };
}

if (!customElements.get('ncc-review-dialog')) customElements.define('ncc-review-dialog', ReviewDialog);
